'use client';

import type { RowData } from '@tanstack/react-table';
import type { Column } from '@/lib/table-features';
import * as React from 'react';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { PlusCircle, XCircle } from 'lucide-react';

interface Range {
  min: number;
  max: number;
}

type RangeValue = [number, number];

function getIsValidRange(value: unknown): value is RangeValue {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number'
  );
}

interface DataTableSliderFilterProps<TData extends RowData> {
  column: Column<TData, unknown>;
  title?: string;
}

export function DataTableSliderFilter<TData extends RowData>({
  column,
  title
}: DataTableSliderFilterProps<TData>) {
  const id = React.useId();
  const tc = useTranslations('common');
  const [open, setOpen] = React.useState(false);

  const columnFilterValue = getIsValidRange(column.getFilterValue())
    ? (column.getFilterValue() as RangeValue)
    : undefined;

  const defaultRange = column.columnDef.meta?.range;
  const unit = column.columnDef.meta?.unit;

  const { min, max, step } = React.useMemo<Range & { step: number }>(() => {
    let minValue = 0;
    let maxValue = 100;

    if (defaultRange && getIsValidRange(defaultRange)) {
      [minValue, maxValue] = defaultRange;
    } else {
      const values = column.getFacetedMinMaxValues();
      if (values && Array.isArray(values) && values.length === 2) {
        const [facetMinValue, facetMaxValue] = values;
        if (
          typeof facetMinValue === 'number' &&
          typeof facetMaxValue === 'number'
        ) {
          minValue = facetMinValue;
          maxValue = facetMaxValue;
        }
      }
    }

    const rangeSize = maxValue - minValue;
    const step =
      rangeSize <= 20
        ? 1
        : rangeSize <= 100
          ? Math.ceil(rangeSize / 20)
          : Math.ceil(rangeSize / 50);

    return { min: minValue, max: maxValue, step };
  }, [column, defaultRange]);

  // Draft state — changes are not committed to the column filter until Apply.
  const [draft, setDraft] = React.useState<RangeValue>(
    () => columnFilterValue ?? [min, max]
  );

  // Re-sync draft whenever the popover opens (or the underlying filter changes
  // externally, e.g. via Reset filters).
  React.useEffect(() => {
    if (open) {
      setDraft(columnFilterValue ?? [min, max]);
    }
  }, [open, columnFilterValue, min, max]);

  const formatValue = React.useCallback((value: number) => {
    return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }, []);

  const onFromInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      if (raw === '') {
        setDraft(([, to]) => [min, to]);
        return;
      }
      const numValue = Number(raw);
      if (Number.isNaN(numValue)) return;
      const clamped = Math.min(Math.max(numValue, min), max);
      setDraft(([, to]) => [clamped, Math.max(clamped, to)]);
    },
    [min, max]
  );

  const onToInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      if (raw === '') {
        setDraft(([from]) => [from, max]);
        return;
      }
      const numValue = Number(raw);
      if (Number.isNaN(numValue)) return;
      const clamped = Math.min(Math.max(numValue, min), max);
      setDraft(([from]) => [Math.min(clamped, from), clamped]);
    },
    [min, max]
  );

  const onSliderValueChange = React.useCallback((value: RangeValue) => {
    if (Array.isArray(value) && value.length === 2) {
      setDraft(value);
    }
  }, []);

  const onApply = React.useCallback(() => {
    // Treat a full-range draft as "no filter" so the chip clears.
    if (draft[0] === min && draft[1] === max) {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue(draft);
    }
    setOpen(false);
  }, [column, draft, min, max]);

  const onClear = React.useCallback(() => {
    setDraft([min, max]);
    column.setFilterValue(undefined);
  }, [column, min, max]);

  const onTriggerReset = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setDraft([min, max]);
      column.setFilterValue(undefined);
    },
    [column, min, max]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant='outline' size='sm' className='border-dashed'>
          {columnFilterValue ? (
            <div
              role='button'
              aria-label={`Clear ${title} filter`}
              tabIndex={0}
              className='focus-visible:ring-ring rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-1 focus-visible:outline-none'
              onClick={onTriggerReset}
            >
              <XCircle />
            </div>
          ) : (
            <PlusCircle />
          )}
          <span>{title}</span>
          {columnFilterValue ? (
            <>
              <Separator
                orientation='vertical'
                className='mx-0.5 data-[orientation=vertical]:h-4'
              />
              {formatValue(columnFilterValue[0])} -{' '}
              {formatValue(columnFilterValue[1])}
              {unit ? ` ${unit}` : ''}
            </>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='flex w-auto flex-col gap-4'>
        <div className='flex flex-col gap-3'>
          <p className='leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70'>
            {title}
          </p>
          <div className='flex items-center gap-4'>
            <Label htmlFor={`${id}-from`} className='sr-only'>
              From
            </Label>
            <div className='relative'>
              <Input
                id={`${id}-from`}
                type='number'
                aria-valuemin={min}
                aria-valuemax={max}
                inputMode='numeric'
                pattern='[0-9]*'
                placeholder={min.toString()}
                min={min}
                max={max}
                value={draft[0]?.toString()}
                onChange={onFromInputChange}
                className={cn('h-8 w-28', unit && 'pe-14')}
              />
              {unit && (
                <span className='bg-accent text-muted-foreground pointer-events-none absolute end-0 top-0 bottom-0 flex items-center rounded-e-md px-2 text-sm'>
                  {unit}
                </span>
              )}
            </div>
            <Label htmlFor={`${id}-to`} className='sr-only'>
              to
            </Label>
            <div className='relative'>
              <Input
                id={`${id}-to`}
                type='number'
                aria-valuemin={min}
                aria-valuemax={max}
                inputMode='numeric'
                pattern='[0-9]*'
                placeholder={max.toString()}
                min={min}
                max={max}
                value={draft[1]?.toString()}
                onChange={onToInputChange}
                className={cn('h-8 w-28', unit && 'pe-14')}
              />
              {unit && (
                <span className='bg-accent text-muted-foreground pointer-events-none absolute end-0 top-0 bottom-0 flex items-center rounded-e-md px-2 text-sm'>
                  {unit}
                </span>
              )}
            </div>
          </div>
          <Label htmlFor={`${id}-slider`} className='sr-only'>
            {title} slider
          </Label>
          <Slider
            id={`${id}-slider`}
            min={min}
            max={max}
            step={step}
            value={draft}
            onValueChange={onSliderValueChange}
          />
        </div>
        <div className='flex items-center gap-2'>
          <Button
            aria-label={`Clear ${title} filter`}
            variant='outline'
            size='sm'
            className='flex-1'
            onClick={onClear}
          >
            {tc('clear')}
          </Button>
          <Button
            aria-label={`Apply ${title} filter`}
            size='sm'
            className='flex-1'
            onClick={onApply}
          >
            {tc('apply')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
